import { useState } from 'react';
import { UserLevel, SubscriptionLevel } from '../lib/api';

interface AvatarProps {
  name: string;
  profile_image_url?: string | null;
  user_level?: UserLevel;
  subscription_level?: SubscriptionLevel | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showGoldRing?: boolean;
}

export default function Avatar({
  name,
  profile_image_url,
  user_level,
  size = 'md',
  className = ''
}: AvatarProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Size configurations
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg'
  };

  // Generate initials from name
  const initials = name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Determine background color based on user level
  const getBackgroundColor = () => {
    switch (user_level) {
      case 'founder':
        return 'bg-gradient-to-br from-yellow-400 to-yellow-600'; // Gold gradient for founders
      case 'free':
      default:
        return 'bg-gradient-to-br from-oro-border to-oro-surface-alt'; // Muted for free users
    }
  };

  // Determine if should show gold ring
  // Future feature: Re-enable for premium tier indicator, achievement badge, etc.
  // const shouldShowGoldRing = showGoldRing && (user_level === 'founder');
  const shouldShowGoldRing = false; // Temporarily disabled - reserved for future feature

  // Show image if we have a src, haven't errored, and want to attempt loading
  const showImage = profile_image_url && !imageError;
  // Show initials if we don't have an image src, image failed to load, or image hasn't loaded yet
  const showInitials = !profile_image_url || imageError || (!imageLoaded && showImage);

  const avatarClasses = `
    ${sizeClasses[size]} 
    ${className}
    rounded-full 
    relative
    flex items-center justify-center 
    font-semibold text-white 
    border-2
    ${shouldShowGoldRing ? 'border-yellow-400 shadow-lg shadow-yellow-400/20' : 'border-transparent'}
    transition-all duration-200
    overflow-hidden
  `.trim();

  return (
    <div className={avatarClasses}>
      {/* Image */}
      {showImage && (
        <img
          src={profile_image_url}
          alt={`${name}'s avatar`}
          className="absolute inset-0 w-full h-full object-cover"
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            setImageError(true);
            setImageLoaded(false);
          }}
        />
      )}
      
      {/* Initials fallback */}
      {showInitials && (
        <div 
          className={`
            absolute inset-0 flex items-center justify-center
            ${getBackgroundColor()}
            ${showImage && !imageLoaded ? 'opacity-50' : ''}
          `}
        >
          {initials}
        </div>
      )}
    </div>
  );
}